import React, { useState, useMemo, useEffect } from 'react';
import { Grid, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ProductGrid from '../components/ProductGrid';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { products } from '../mockData';
import axios from 'axios';

const ToileAuMetrePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null); // Sera défini à la première collection chargée
  const [collections, setCollections] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [realProducts, setRealProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Charger les collections depuis l'API et définir la première comme sélectionnée
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/collections/`);
        const collectionsData = response.data || [];
        setCollections(collectionsData);
        
        // Définir la première collection comme sélectionnée par défaut
        if (collectionsData.length > 0) {
          setSelectedCollection(collectionsData[0].name);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des collections:', error);
        setCollections([]);
      }
    };
    
    fetchCollections();
  }, []);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/suppliers/`);
        setSuppliers(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs:', error);
        setSuppliers([]);
      }
    };
    
    fetchSuppliers();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/products/`);
        
        const adaptedProducts = response.data.map(product => ({
          id: product.id,
          name: product.ref_frs || product.code1 || product.full_code || 'Produit',
          collection: product.gamme || 'N/A',
          brand: product.supplier || 'N/A',
          price: 0,
          image: product.image || null,
          slug: product.slug || product.id,
          thumbnail: product.thumbnail || product.image || null,
          supplier: product.supplier,
          category: product.category,
          code1: product.code1,
          ref_frs: product.ref_frs
        }));
        
        setRealProducts(adaptedProducts);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (realProducts.length === 0) return [];
    let filtered = [...realProducts];

    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection === selectedCollection);
    }

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    if (selectedColors.length > 0) {
      filtered = filtered.filter(p => selectedColors.includes(p.color));
    }

    if (selectedPatterns.length > 0) {
      filtered = filtered.filter(p => selectedPatterns.includes(p.pattern));
    }

    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch(sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }

    return filtered;
  }, [selectedCollection, selectedBrands, selectedColors, selectedPatterns, searchQuery, sortBy, realProducts]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer" onClick={() => navigate('/')}>Accueil</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900">Achetez votre toile de store au mètre</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <Sidebar 
            selectedBrands={selectedBrands}
            setSelectedBrands={setSelectedBrands}
            selectedColors={selectedColors}
            setSelectedColors={setSelectedColors}
            selectedPatterns={selectedPatterns}
            setSelectedPatterns={setSelectedPatterns}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollection}
          />

          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Achetez votre toile de store au mètre</h1>

            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <List size={20} />
                </button>
                {loading ? (
                  <span className="text-sm text-gray-600">Chargement des produits...</span>
                ) : (
                  <span className="text-sm text-gray-600">Il y a {filteredProducts.length} produits.</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Trier par:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-celadon"
                >
                  <option value="relevance">Pertinence</option>
                  <option value="name-asc">Nom, A à Z</option>
                  <option value="name-desc">Nom, Z à A</option>
                  <option value="price-asc">Prix, croissant</option>
                  <option value="price-desc">Prix, décroissant</option>
                </select>
              </div>
            </div>

            <ProductGrid products={filteredProducts} viewMode={viewMode} productType="toile-au-metre" />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ToileAuMetrePage;
